import type { useApiClient } from '../../../helpers/apiClient.helper';

type ApiClient = ReturnType<typeof useApiClient>;

export interface PickStatsOddsValue {
  value: string;
  odd: number;
}

export interface PickStatsOddsMarket {
  id: number;
  name: string;
  values: PickStatsOddsValue[];
}

export interface PickStatsScoreEntry {
  home: number;
  away: number;
  count: number;
  pct: number;
}

export interface PickStatsMatchInfo {
  id: string;
  kickoffTime: string;
  status: string;
  homeScore: number | null;
  awayScore: number | null;
  /** 90-minute (regular-time) score; exact-hit judging uses this, not the ET-inclusive final. */
  fullTimeHomeScore?: number | null;
  fullTimeAwayScore?: number | null;
  penaltyHomeScore?: number | null;
  penaltyAwayScore?: number | null;
  liveMinute: number | null;
  homeTeam: string;
  awayTeam: string;
  homeTeamShort: string;
  awayTeamShort: string;
  homeCountryCode: string | null;
  awayCountryCode: string | null;
}

export interface PickStatsMemberPick {
  userId: string;
  displayName: string;
  avatarId: string | null;
  predictedHomeScore: number;
  predictedAwayScore: number;
}

export interface MatchPickStatsData {
  match: PickStatsMatchInfo;
  totalPicks: number;
  outcome: { home: number; draw: number; away: number };
  scores: PickStatsScoreEntry[];
  odds: PickStatsOddsMarket[];
  memberPicks?: PickStatsMemberPick[];
}

export async function fetchMatchPickStats(
  apiClient: ApiClient,
  competitionId: string,
  matchId: string,
  groupId?: string,
): Promise<MatchPickStatsData> {
  const params = groupId ? `?groupId=${encodeURIComponent(groupId)}` : '';
  const response = await apiClient<{ success: boolean; data: MatchPickStatsData; error?: string }>(
    `/competitions/${competitionId}/matches/${matchId}/pick-stats${params}`,
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.error ?? 'Failed to fetch pick stats');
  return payload.data;
}
